import { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Camera, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Separator } from '@/shared/components/ui/separator';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { FormFieldTextarea } from '@/shared/components/custom/form-field/FormFieldTextarea';
import { ColorPickerPopover } from '@/shared/components/custom/color-picker/ColorPickerPopover';
import { OrgAvatar } from '@/shared/components/custom/avatars/OrgAvatar';
import { useOrganizationStore } from '@/core/stores/organization.store';
import {
  useOrganization,
  useUpdateOrganization,
  useDeleteOrganization,
} from '@/features/organizations/public';
import {
  updateOrganizationInfoSchema,
  type UpdateOrganizationInfoInput,
} from '@/features/organizations/public';
import { getColorValue } from '@/core/lib/colors';
import { OrgInfoSkeleton } from './org/OrgInfoSkeleton';
import { OrgDeleteDialog } from './org/OrgDeleteDialog';
import { useUploadOrgAvatar, useDeleteOrgAvatar } from '@/features/organizations/public';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useToast } from '@/shared/hooks/useToast';
import { FileUploader } from '@/shared/components/custom/file-uploader/FileUploader';
import { ImageCropDialog } from '@/shared/components/custom/image-crop/ImageCropDialog';
import { SettingsSectionHeader } from '../SettingsSectionHeader';
import { DangerZone } from '../DangerZone';

const DEFAULT_COLOR = 'indigo-violet';

export function OrgInfoSection() {
  const { t } = useTranslation('settings');
  const { t: tOrg } = useTranslation('organizations');

  const activeOrganizationId = useOrganizationStore((s) => s.activeOrganizationId);

  const { data: org, isLoading, isError } = useOrganization(activeOrganizationId);
  const updateOrg = useUpdateOrganization(activeOrganizationId ?? '');
  const deleteOrg = useDeleteOrganization();
  const uploadAvatar = useUploadOrgAvatar();
  const deleteAvatar = useDeleteOrgAvatar();
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();
  const { toast } = useToast();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const form = useForm<UpdateOrganizationInfoInput>({
    resolver: zodResolver(updateOrganizationInfoSchema(tOrg)),
    defaultValues: { name: '', slug: '', description: '', websiteUrl: '', color: DEFAULT_COLOR },
  });

  const {
    formState: { isDirty, isSubmitting },
  } = form;

  const watchedName = form.watch('name');
  const watchedColor = form.watch('color') ?? DEFAULT_COLOR;

  useEffect(() => {
    if (!org) return;
    form.reset({
      name: org.name ?? '',
      slug: org.slug ?? '',
      description: org.description ?? '',
      websiteUrl: org.websiteUrl ?? '',
      color: org.color ?? DEFAULT_COLOR,
    });
  }, [org, form]);

  const onSubmit = form.handleSubmit(async (data) => {
    try {
      const payload = Object.fromEntries(
        Object.entries(data).filter(([, v]) => v !== '' && v !== undefined),
      ) as typeof data;
      await updateOrg.mutateAsync(payload);
      form.reset(data);
    } catch {
      // global handler shows error toast
    }
  });

  const openUploaderRef = useRef<(() => void) | null>(null);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);

  const handleAvatarFilesAccepted = (_files: File[], previewUrls: string[]) => {
    if (previewUrls[0]) setCropImageSrc(previewUrls[0]);
  };

  const handleAvatarFilesRejected = () => {
    toast.error({ title: t('profile.avatar.typeError') });
  };

  const handleCropConfirm = (blob: Blob) => {
    setCropImageSrc(null);
    if (activeOrganizationId) {
      uploadAvatar.mutate({
        tenantId: activeOrganizationId,
        file: new File([blob], 'avatar.jpg', { type: blob.type }),
      });
    }
  };

  const handleCropCancel = () => {
    setCropImageSrc(null);
  };

  const handleAvatarRemove = async () => {
    if (!activeOrganizationId) return;
    const result = await confirm({
      title: t('orgInfo.avatarRemove'),
      description: t('orgInfo.avatarRemove'),
      variant: 'destructive',
    });
    if (result.confirmed) {
      deleteAvatar.mutate(activeOrganizationId);
    }
  };

  if (isLoading) return <OrgInfoSkeleton />;

  if (isError) {
    return (
      <div data-testid="settings-org-info-error" className="text-sm text-destructive py-4">
        {t('orgInfo.loadError')}
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="settings-org-info-section">
      {/* ── 1. Datos de la organización ───────────────────────── */}
      <div>
        <div className="pb-6">
          <SettingsSectionHeader>{t('orgInfo.dataTitle')}</SettingsSectionHeader>
          <p className="text-sm text-muted-foreground mt-1">{t('orgInfo.dataDescription')}</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-10">
          <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-8 items-start">
            <div className="flex justify-center">
              <FileUploader
                accept={{ 'image/*': ['.jpg', '.jpeg', '.png', '.webp'] }}
                maxSize={2 * 1024 * 1024}
                onFilesAccepted={handleAvatarFilesAccepted}
                onFilesRejected={handleAvatarFilesRejected}
              >
                {({ getInputProps, open }) => {
                  openUploaderRef.current = open;
                  return (
                    <div className="relative" data-testid="settings-org-info-avatar">
                      <OrgAvatar
                        name={watchedName || '?'}
                        color={watchedColor || DEFAULT_COLOR}
                        src={org?.avatarUrl ?? undefined}
                        size="2xl"
                        className="ring-2 ring-background"
                      />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="absolute bottom-1 right-1 flex size-7 items-center justify-center rounded-full bg-foreground text-background shadow-md hover:bg-foreground/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            data-testid="settings-org-info-avatar-edit"
                          >
                            <Camera className="size-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="center">
                          <DropdownMenuItem onClick={() => openUploaderRef.current?.()}>
                            {t('orgInfo.avatarUpload')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            variant="destructive"
                            onClick={handleAvatarRemove}
                            disabled={!org?.avatarUrl}
                          >
                            {t('orgInfo.avatarRemove')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <input {...getInputProps()} data-testid="settings-org-info-avatar-input" />
                    </div>
                  );
                }}
              </FileUploader>
              <ImageCropDialog
                open={!!cropImageSrc}
                imageSrc={cropImageSrc}
                cropShape="rect"
                onConfirm={handleCropConfirm}
                onCancel={handleCropCancel}
                labels={{
                  title: t('orgInfo.avatarCrop.title'),
                  zoom: t('orgInfo.avatarCrop.zoom'),
                  rotate: t('orgInfo.avatarCrop.rotate'),
                  confirm: t('orgInfo.avatarCrop.confirm'),
                  cancel: t('orgInfo.avatarCrop.cancel'),
                }}
              />
            </div>

            <div className="space-y-6">
              <div className="space-y-3">
                <p className="text-sm font-medium text-foreground">{t('orgInfo.colorLabel')}</p>
                <div className="flex items-center gap-3">
                  <Controller
                    control={form.control}
                    name="color"
                    render={({ field }) => (
                      <ColorPickerPopover
                        value={field.value}
                        onSelect={(token) => field.onChange(token)}
                        trigger={
                          <button
                            type="button"
                            data-testid="settings-org-info-color-trigger"
                            aria-label={t('orgInfo.colorLabel')}
                            className="size-8 rounded-full shrink-0 ring-2 ring-offset-2 ring-offset-background ring-border hover:ring-primary transition-all focus-visible:outline-none focus-visible:ring-primary"
                            style={{ background: getColorValue(field.value) || field.value }}
                          />
                        }
                      />
                    )}
                  />
                  <p className="text-xs text-muted-foreground">{t('orgInfo.colorDescription')}</p>
                </div>
              </div>

              <FormFieldInput
                control={form.control}
                name="name"
                label={t('orgInfo.name')}
                floatingLabel
                data-testid="settings-org-info-name-input"
              />

              <div className="space-y-1">
                <FormFieldInput
                  control={form.control}
                  name="slug"
                  label={t('orgInfo.slug')}
                  floatingLabel
                  data-testid="settings-org-info-slug-input"
                />
                <p className="text-xs text-muted-foreground px-1">{t('orgInfo.slugHint')}</p>
              </div>

              <FormFieldTextarea
                control={form.control}
                name="description"
                label={t('orgInfo.descriptionField')}
                floatingLabel
                rows={3}
                data-testid="settings-org-info-description-input"
              />

              <FormFieldInput
                control={form.control}
                name="websiteUrl"
                label={t('orgInfo.websiteUrl')}
                floatingLabel
                data-testid="settings-org-info-website-input"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={!isDirty || isSubmitting}
              data-testid="settings-org-info-save-button"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('orgInfo.saving')}
                </>
              ) : (
                t('orgInfo.save')
              )}
            </Button>
          </div>
        </form>
      </div>

      <Separator />

      {/* ── 2. Datos de cuenta ────────────────────────────────── */}
      <div>
        <div className="pb-6">
          <SettingsSectionHeader>{t('orgInfo.accountTitle')}</SettingsSectionHeader>
          <p className="text-sm text-muted-foreground mt-1">{t('orgInfo.accountDescription')}</p>
        </div>

        <dl className="space-y-4" data-testid="settings-org-info-account-section">
          <div className="flex items-center justify-between">
            <dt className="text-sm text-muted-foreground">{t('orgInfo.tierLabel')}</dt>
            <dd
              className="text-sm font-medium text-foreground capitalize"
              data-testid="settings-org-info-tier"
            >
              {org?.tier ?? '—'}
            </dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-sm text-muted-foreground">{t('orgInfo.membersCountLabel')}</dt>
            <dd
              className="text-sm font-medium text-foreground"
              data-testid="settings-org-info-members-count"
            >
              {org?.membersCount ?? '—'}
            </dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-sm text-muted-foreground">{t('orgInfo.memberLimitLabel')}</dt>
            <dd
              className="text-sm font-medium text-foreground"
              data-testid="settings-org-info-member-limit"
            >
              {org?.memberLimit ?? '—'}
            </dd>
          </div>
        </dl>
      </div>

      <Separator />

      {/* ── 3. Zona de peligro ────────────────────────────────── */}
      <DangerZone
        title={t('orgInfo.deleteTitle')}
        description={t('orgInfo.deleteDescription')}
        actionTitle={t('orgInfo.deleteActionTitle')}
        actionDescription={t('orgInfo.deleteActionDescription')}
        buttonLabel={t('orgInfo.deleteButton')}
        onAction={() => setDeleteDialogOpen(true)}
        testId="settings-org-delete-zone"
        buttonTestId="settings-org-delete-button"
      />

      <OrgDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        orgName={org?.name ?? ''}
        isPending={deleteOrg.isPending}
        onConfirm={async () => {
          if (!activeOrganizationId) return;
          await deleteOrg.mutateAsync(activeOrganizationId);
        }}
      />

      <ConfirmDialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
        title={options?.title ?? ''}
        description={options?.description}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
}
